import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, useMarkAllRead, useMarkRead } from '@/hooks/use-notifications';
import { formatDistanceToNow } from '@/lib/utils';
import { cn } from '@/lib/utils';
import {
  Bell,
  BellOff,
  Loader2,
  CheckCheck,
  MessageSquare,
  UserPlus,
  FolderKanban,
  CheckSquare,
  FileText,
  Ticket,
  Receipt,
  Clock,
  ExternalLink,
  CheckCircle2,
} from 'lucide-react';

const typeIcons: Record<string, React.ElementType> = {
  task_assigned: CheckSquare,
  status_change: FolderKanban,
  new_comment: MessageSquare,
  new_ticket: Ticket,
  ticket_update: Ticket,
  file_uploaded: FileText,
  invoice_generated: Receipt,
  invoice_paid: Receipt,
  deposit_invoice: Receipt,
  quote_accepted: CheckCircle2,
  time_approved: Clock,
  member_added: UserPlus,
};

/** Group notifications by recency */
interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  isRead: boolean;
  createdAt: string;
}

function groupByTime(notifications: NotificationItem[]): Record<string, NotificationItem[]> {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const weekAgo = new Date(today.getTime() - 7 * 86400000);

  const groups: Record<string, NotificationItem[]> = { Today: [], Yesterday: [], 'This Week': [], Earlier: [] };

  for (const n of notifications) {
    const date = new Date(n.createdAt);
    if (date >= today) groups.Today.push(n);
    else if (date >= yesterday) groups.Yesterday.push(n);
    else if (date >= weekAgo) groups['This Week'].push(n);
    else groups.Earlier.push(n);
  }

  // Remove empty groups
  Object.keys(groups).forEach((key) => {
    if (groups[key].length === 0) delete groups[key];
  });

  return groups;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data, isLoading } = useNotifications();
  const markAllRead = useMarkAllRead();
  const markRead = useMarkRead();

  const notifications = data?.data?.notifications || [];
  const unreadCount: number = data?.data?.unreadCount ?? 0;

  const grouped = useMemo(() => groupByTime(notifications), [notifications]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleNotificationClick = async (notification: any) => {
    if (!notification.isRead) {
      await markRead.mutateAsync(notification.id);
    }
    setOpen(false);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllRead.mutateAsync();
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/activity');
  };

  const IconForType = (type: string): React.ElementType => typeIcons[type] || Bell;

  return (
    <div ref={dropdownRef} className="relative">
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)] group"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell size={20} className="transition-transform duration-200 group-hover:scale-110" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1 shadow-lg animate-in zoom-in-0">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown — animated */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in-0 slide-in-from-top-2 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-[var(--border-color)]">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-dyndev-green/10 text-dyndev-green">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  disabled={markAllRead.isPending}
                  className="text-[10px] text-dyndev-green hover:text-dyndev-green/80 transition-colors flex items-center gap-1 px-2 py-1 rounded-md hover:bg-dyndev-green/5"
                >
                  {markAllRead.isPending ? (
                    <Loader2 size={10} className="animate-spin" />
                  ) : (
                    <CheckCheck size={12} />
                  )}
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-dyndev-green" />
                  <p className="text-xs text-[var(--text-secondary)]">Loading notifications...</p>
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                <div className="w-12 h-12 rounded-full bg-dyndev-green/10 flex items-center justify-center mb-3">
                  <BellOff size={20} className="text-dyndev-green" />
                </div>
                <p className="text-sm font-medium mb-1">All caught up!</p>
                <p className="text-[11px] text-[var(--text-secondary)] mb-4 max-w-[200px]">
                  You'll see notifications here when there's activity on your projects, tasks, or tickets.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--border-color)]">
                {Object.entries(grouped).map(([groupName, groupItems]) => (
                  <div key={groupName}>
                    {/* Time group header */}
                    <div className="sticky top-0 z-10 px-3 py-1.5 bg-[var(--bg-secondary)]/95 backdrop-blur-sm border-b border-[var(--border-color)]">
                      <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                        {groupName}
                      </span>
                    </div>
                    {groupItems.map((notification: any) => {
                      const Icon = IconForType(notification.type);
                      return (
                        <button
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className={cn(
                            'w-full text-left flex gap-3 p-3 transition-all duration-150 hover:bg-[var(--bg-primary)] relative',
                            !notification.isRead && 'bg-dyndev-green/[0.02]',
                          )}
                        >
                          {/* Unread indicator dot */}
                          {!notification.isRead && (
                            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-dyndev-green" />
                          )}

                          <div className={cn(
                            'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                            !notification.isRead ? 'bg-dyndev-green/10' : 'bg-[var(--bg-primary)]',
                          )}>
                            <Icon size={14} className={!notification.isRead ? 'text-dyndev-green' : 'text-[var(--text-secondary)]'} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className={cn(
                                'text-xs truncate',
                                !notification.isRead ? 'font-semibold' : 'font-medium',
                              )}>
                                {notification.title}
                              </p>
                              <span className="text-[9px] text-[var(--text-secondary)] whitespace-nowrap flex-shrink-0">
                                {formatDistanceToNow(notification.createdAt)}
                              </span>
                            </div>
                            <p className="text-[10px] text-[var(--text-secondary)] line-clamp-2 mt-0.5 leading-relaxed">
                              {notification.message}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer — View all */}
          <button
            onClick={handleViewAll}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 border-t border-[var(--border-color)] text-xs text-[var(--text-secondary)] hover:text-dyndev-green hover:bg-dyndev-green/5 transition-colors"
          >
            <ExternalLink size={12} />
            View all activity
          </button>
        </div>
      )}
    </div>
  );
}
